import CloseIcon from '@mui/icons-material/Close';
import clsx from 'clsx';
import FocusTrap from 'focus-trap-react';
import React, { ReactNode, useEffect, useRef } from 'react';

import useMountTransition from '../../hooks/useMountTransition';
import styles from './Drawer.module.scss';

interface Props {
    children: ReactNode;
    className?: string;
    header: ReactNode;
    isOpen: boolean;
    onClose: () => void;
}

const Drawer: React.FC<Props> = ({ children, className, header, isOpen, onClose }) => {
    const isTransitioning = useMountTransition(isOpen, 300);
    const bodyRef = useRef(document.querySelector('body') as HTMLBodyElement);

    // Prevent page scrolling when the drawer is open
    useEffect(() => {
        const updatePageScroll = () => {
            if (isOpen) {
                bodyRef.current.style.overflow = 'hidden';
            } else {
                bodyRef.current.style.overflow = '';
            }
        };
        updatePageScroll();
    }, [isOpen]);

    // the drawer should be closable by pressing the escape key.
    useEffect(() => {
        const onKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            window.addEventListener('keyup', onKeyPress);
        }

        return () => {
            window.removeEventListener('keyup', onKeyPress);
        };
    }, [isOpen, onClose]);

    if (!isTransitioning && !isOpen) {
        return null;
    }

    return (
        <FocusTrap
            active={isOpen}
            focusTrapOptions={{
                fallbackFocus: '#drawerContainer',
            }}>
            <div
                aria-hidden={isOpen ? 'false' : 'true'}
                className={clsx(styles['drawer-container'], className, {
                    [styles.open]: isOpen,
                    [styles.in]: isTransitioning,
                })}
                id="drawerContainer"
                data-testid="drawer-container"
                tabIndex={-1}>
                <div className={clsx(styles.drawer, styles.left)} role="dialog">
                    <div className={styles['drawer-header']}>
                        {header}
                        <button
                            onClick={onClose}
                            className={styles['drawer-close-button']}
                            data-testid="drawer-close-button"
                            aria-label="close side drawer">
                            <CloseIcon width="24" />
                        </button>
                    </div>
                    {children}
                </div>
                <div
                    className={styles.backdrop}
                    data-testid="drawer-backdrop"
                    onClick={onClose}
                    onKeyPress={onClose}
                    role="presentation"
                />
            </div>
        </FocusTrap>
    );
};

export default Drawer;
